// ── 날짜순 뷰 데이터 변환 ─────────────────────────────────────────────────────
// clients + unmatched + records + cancelEvents → 날짜 내림차순 flat 배열

import { contractItemsLabel, contractServiceLabel } from './utils';
import { toDateString } from '../../../utils/date';

const CONTRACT_CHANGE_LABEL = {
  cancel: '계약취소',
  restore: '계약완료',
};

function eventTimeText(eventAt = '') {
  return String(eventAt || '').slice(11, 16);
}

function sortContractEvents(a, b) {
  const at = String(a.eventAt || a.eventDate || '');
  const bt = String(b.eventAt || b.eventDate || '');
  if (at !== bt) return at.localeCompare(bt);
  return Number(a.id || 0) - Number(b.id || 0);
}

function mapContractChangeEvent(ev) {
  const ticketLbl = contractItemsLabel(ev.contractItems);
  const dateStr = ev.contractDate ? ev.contractDate.slice(0, 10) : '';
  const timeStr = eventTimeText(ev.eventAt);
  const actionLabel = CONTRACT_CHANGE_LABEL[ev.action] || '상태변경';
  const direction = ev.action === 'cancel' ? '계약완료 → 계약취소' : '계약취소 → 계약완료';
  return {
    id: ev.id,
    action: ev.action,
    actionLabel,
    direction,
    eventAt: ev.eventAt || '',
    eventTime: timeStr,
    actorName: ev.actorName || '',
    source: ev.source || '',
    label: ticketLbl !== '—' ? ticketLbl : null,
    sub: `${direction}${dateStr ? ` (${dateStr})` : ''}${timeStr ? ` · ${timeStr}` : ''}`,
  };
}

function buildContractChangeTimelineItems(cancelEvents = []) {
  const grouped = new Map();
  for (const ev of cancelEvents || []) {
    const key = ev.contractId
      ? `${ev.contractId}:${ev.eventDate || ''}`
      : `event:${ev.id}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(ev);
  }

  const items = [];
  for (const group of grouped.values()) {
    const sorted = [...group].sort(sortContractEvents);
    const first = sorted[0] || {};
    const last = sorted[sorted.length - 1] || first;
    const details = sorted.map(mapContractChangeEvent);
    const lastDetail = details[details.length - 1] || mapContractChangeEvent(last);

    if (details.length === 1) {
      items.push({
        date: first.eventDate,
        type: first.action,
        label: lastDetail.label,
        sub: lastDetail.sub,
        store: first.clientName || null,
        origAmt: null,
        amount: 0,
        matched: true,
        key: `cancel-${first.id}`,
      });
      continue;
    }

    const dateStr = first.contractDate ? first.contractDate.slice(0, 10) : '';
    items.push({
      date: first.eventDate,
      type: last.action,
      label: lastDetail.label,
      sub: `계약취소↔계약완료 ${details.length}회 · 최종 ${lastDetail.actionLabel}${dateStr ? ` (${dateStr})` : ''}`,
      store: first.clientName || null,
      origAmt: null,
      amount: 0,
      matched: true,
      contractChangeEvents: details,
      key: `cancel-group-${first.contractId || first.id}-${first.eventDate || ''}`,
    });
  }
  return items;
}

/**
 * 모든 항목(계약차감·택배비·홍보물·카드·카드업로드·전화·입금·정산지급·취소/복원)을
 * 날짜 내림차순으로 정렬한 flat 배열로 변환한다.
 *
 * @param {Array}  clients      - 매장별 집계 객체 배열
 * @param {Object} unmatched    - { card, card_upload, phone, payments } 미연결 결제 (camelCase)
 * @param {Array}  records      - 정산지급 기록 배열
 * @param {Array}  cancelEvents - 계약 취소/복원 이벤트 배열 (useSettlement에서 전달)
 */
export function buildTimeline(clients, unmatched, records = [], cancelEvents = [], shareIncome = { items: [] }) {
  const items = [];

  for (const client of clients) {
    const { minus, plus } = client;

    // 계약 차감 — contract_items 배열 기반으로 표시
    for (const c of minus.contracts) {
      items.push({
        date: c.contractDate, type: 'contract',
        label: contractItemsLabel(c.contractItems),
        sub: contractServiceLabel(c.contractItems) ? `서비스 ${contractServiceLabel(c.contractItems)}` : null,
        store: client.name, origAmt: null, amount: -c.fee, matched: true,
        isCancelled: c.isCancelled || false,  // 계약취소 칩 표시용
        key: `contract-${c.id}`,
      });
      // 상담자 배분 차감 — 계약일 기준으로 계약 항목 바로 아래에 배치
      if (!c.isCancelled && c.consultantShare?.shareAmount != null) {
        items.push({
          date: c.contractDate, type: 'consultant_share',
          label: `${c.consultantShare.consultantName}  ${c.consultantShare.shareRate}% 차감`,
          sub: null,
          store: client.name, origAmt: null,
          amount: -c.consultantShare.shareAmount, matched: true,
          key: `cs-${c.id}`,
        });
      }
    }

    // 택배비 차감 — 계약일(contractDate) 기준으로 날짜 배치 (계약차감 바로 아래)
    // trackingText: 모바일 날짜순 뷰 CopyTrackingButton용 — PC는 label 텍스트로 표시
    minus.delivery.forEach((d, i) => {
      items.push({
        date: d.contractDate || null, type: 'delivery',
        label: d.carrier && d.trackingNo ? `${d.carrier} ${d.trackingNo}` : '—',
        trackingText: d.carrier && d.trackingNo ? `${d.carrier} ${d.trackingNo}` : null,
        sub: null,
        store: client.name, origAmt: null, amount: -d.fee, matched: true,
        key: `delivery-${client.id}-${i}`,
      });
    });

    // 홍보물 차감
    // trackingText: 모바일 날짜순 뷰 CopyTrackingButton용 — PC는 sub 텍스트로 표시
    for (const po of minus.promotional) {
      for (const i of po.items) {
        items.push({
          date: po.receivedDate, type: 'promo',
          label: `${i.item_name} ${i.quantity}개`,
          trackingText: i.carrier && i.tracking_no ? `${i.carrier} ${i.tracking_no}` : null,
          sub: i.carrier && i.tracking_no ? `${i.carrier} ${i.tracking_no}` : null,
          store: client.name, origAmt: null,
          amount: -(Number(i.quantity) * Number(i.unit_price)), matched: true,
          key: `promo-${i.id}`,
        });
      }
    }

    // 홍보물 예정 — 발주/완료 전이라 정산 금액에는 반영하지 않음
    for (const po of (minus.promotionalPending || [])) {
      for (const i of po.items) {
        items.push({
          date: po.receivedDate, type: 'promo_pending',
          label: `${i.item_name} ${i.quantity}개`,
          trackingText: null,
          sub: '정산 미반영',
          store: client.name, origAmt: Number(i.quantity) * Number(i.unit_price),
          amount: 0, matched: true,
          key: `promo-pending-${i.id}`,
        });
      }
    }

    // 카드승인 (연결됨 — 수동 입력: dot_revenue_cards 등)
    for (const c of plus.cards) {
      items.push({
        date: c.transactionDate, type: 'card',
        label: c.cardIssuer || c.merchantName || '—',
        sub: null,
        store: client.name, origAmt: c.amount, amount: c.commission, matched: true,
        key: `card-${c.id}`,
      });
    }

    // 카드업로드 (연결됨 — 엑셀 업로드: revenue_dot_card_approvals / ext_revenue_card_approvals 등)
    // plusTotal에 합산되지만 기존 buildTimeline이 누락하던 항목 — DOT·LNJ 주요 카드 데이터
    for (const c of (plus.cardUploads || [])) {
      items.push({
        date: c.transactionDate, type: 'card',
        label: c.cardIssuer || c.merchantName || '—',
        sub: null,
        store: client.name, origAmt: c.amount, amount: c.commission, matched: true,
        key: `cardup-${c.id}`,
      });
    }

    // 전화승인 (연결됨)
    for (const p of plus.phones) {
      items.push({
        date: p.paymentDate, type: 'phone',
        label: p.cardCompany || '—',
        sub: p.note || null,
        store: client.name, origAmt: p.amount, amount: p.commission, matched: true,
        key: `phone-${p.id}`,
      });
    }

    // 입금 (연결됨)
    for (const d of plus.deposits) {
      items.push({
        date: d.paymentDate, type: 'deposit',
        label: d.payerName || '—',
        sub: d.taxInvoiceLinked && d.taxInvoice
          ? `${d.taxInvoice.issueType || '영수'}계산서` : null,
        store: client.name, origAmt: d.amount, amount: d.commission, matched: true,
        key: `deposit-${d.id}`,
      });
    }
  }

  // 미연결 카드 (수동)
  for (const c of (unmatched.card || [])) {
    items.push({
      date: c.transactionDate, type: 'card',
      label: c.cardIssuer || c.merchantName || '—', sub: null,
      store: null, origAmt: c.amount,
      amount: c.commission, matched: false,
      key: `ucard-${c.id}`,
    });
  }
  // 미연결 카드업로드 (엑셀 업로드 — DOT·LNJ 주요 카드 데이터)
  for (const c of (unmatched.card_upload || [])) {
    items.push({
      date: c.transactionDate, type: 'card',
      label: c.cardIssuer || c.merchantName || '—', sub: null,
      store: null, origAmt: c.amount,
      amount: c.commission, matched: false,
      key: `ucdup-${c.id}`,
    });
  }
  // 미연결 전화
  for (const p of unmatched.phone) {
    items.push({
      date: p.paymentDate, type: 'phone',
      label: p.cardCompany || '—', sub: p.note || null,
      store: null, origAmt: p.amount, amount: p.commission, matched: false,
      key: `uphone-${p.id}`,
    });
  }
  // 미연결 입금
  for (const d of unmatched.payments) {
    items.push({
      date: d.paymentDate, type: 'deposit',
      label: d.payerName || '—', sub: null,
      store: null, origAmt: d.amount, amount: d.commission, matched: false,
      key: `upay-${d.id}`,
    });
  }

  // 정산지급 기록 — 관리자가 입금한 내역 (민트 뱃지, 마이너스 표시)
  for (const r of records) {
    items.push({
      date: toDateString(r.settlement_date),
      type: 'settlement',
      label: r.note || null,  // 배지에 이미 '정산지급' 표시 — 메모 없으면 null로 중복 방지
      sub: null,
      store: null, origAmt: null,
      amount: -Number(r.amount), matched: true,
      key: `settlement-${r.id}`,
    });
  }

  for (const item of buildContractChangeTimelineItems(cancelEvents)) {
    items.push(item);
  }

  // 상담자 배분 수입 — 내 상담자를 배정받아 타 마케터 매장에서 발생한 수입
  for (const i of (shareIncome?.items || [])) {
    items.push({
      date: i.date || null, type: 'consultant_share_income',
      label: `${i.consultantName}  ${i.shareRate}% 배분`,
      sub: i.clientName || null,  // 해당 수입이 발생한 매장명
      store: null, origAmt: null, amount: i.amount, matched: true,
      key: `share-income-${i.contractId || i.clientId}`,
    });
  }

  // 기타 수입 — 매출 관리 > 기타에서 입력된 항목
  for (const c of (unmatched.consultantCharges || [])) {
    items.push({
      date: c.settlementDate || c.settlement_date || null,
      type: 'consultant_charge',
      label: c.label || `${c.consultantName || c.consultant_name || '1:1 직원'} ${c.month || ''}월 급여`,
      sub: null,
      store: null,
      origAmt: null,
      amount: -Math.abs(Number(c.chargeAmount ?? c.charge_amount ?? c.amount ?? 0)),
      matched: true,
      detail: c,
      key: `consultant-charge-${c.id}`,
    });
  }

  for (const o of (unmatched.others || [])) {
    items.push({
      date: o.date || null, type: 'others',
      label: o.description || null,
      sub: o.note || null,
      store: null, origAmt: null, amount: o.amount, matched: true,
      key: `others-${o.id}`,
    });
  }

  // 날짜 내림차순 (날짜 없는 항목은 맨 아래)
  items.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.localeCompare(a.date);
  });

  return items;
}
